import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Tabs,
  Tab,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  Apps as AppsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Info as InfoIcon,
  CloudUpload as CloudUploadIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Place as PlaceIcon,
  Visibility as VisibilityIcon,
  Mouse as MouseIcon,
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIndicatorIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { appBannerService, AppBanner, AppBannerPayload, eventService as adminEventService } from '../services';
import EventTagsAdminPanel from './EventTagsAdminPanel';
import { eventService, EventResponse } from '../../../services/eventService';
import type { Event as AdminEvent } from '../../../types/types';

/** Alinhado ao multer em `backend/src/middlewares/fileUpload.ts` (banner). */
const BANNER_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
const BANNER_UPLOAD_MAX_MB = 5;
const BANNER_UPLOAD_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const BANNER_ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
/** Aviso não bloqueante: ficheiros grandes demoram mais em redes lentas. */
const BANNER_SOFT_SIZE_WARN_BYTES = 1024 * 1024;

function validateBannerUploadFile(file: File): string | null {
  if (!BANNER_ALLOWED_MIME.has(file.type)) {
    return 'Formato não aceite. Envie JPEG, PNG, WebP ou GIF. Não há conversão automática — exporte ou converta antes (ex.: HEIC do iPhone para JPEG no Fotos ou numa ferramenta online).';
  }
  if (file.size > BANNER_UPLOAD_MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return `Ficheiro demasiado grande (${mb} MB). Máximo: ${BANNER_UPLOAD_MAX_MB} MB. Comprima ou reduza a resolução antes de enviar.`;
  }
  return null;
}

/** Referência alinhada a boas práticas web/mobile (hero/banner responsivo, OG-style). */
const BANNER_IDEAL = {
  primarySize: '1200 × 628 px',
  primaryRatio: '≈ 1,91:1 (formato largo, similar a pré-visualização em redes)',
  safeArea: 'Mantenha rostos, logos e mensagens principais na zona central (~60% da largura e altura); as bordas podem ser cortadas em telas estreitas.',
  mobileNote:
    'No celular o app pode exibir a imagem em retângulo mais alto ou com `object-fit: cover`. Se puder, gere uma arte extra em retrato (~800 × 1200 px, proporção ~2:3) só para mobile; se usar um único ficheiro, concentre o conteúdo no centro.',
  fileHint: 'Prefira WebP ou JPEG bem comprimido; idealmente entre ~100 e 300 KB para carregar rápido em 4G.',
  uploadPolicy: `Apenas JPEG, PNG, WebP ou GIF até ${BANNER_UPLOAD_MAX_MB} MB. O servidor não converte formatos — BMP, TIFF, SVG, HEIC, etc. são rejeitados.`,
  textHint:
    'O título da campanha é cadastrado abaixo e pode aparecer no app — não coloque só na imagem texto que precise ser lido (SEO, acessibilidade e leitura em telas pequenas).',
  contrastHint: 'Use contraste forte e tipografia legível; detalhes finos somem em telas pequenas.',
} as const;

function BannerIdealGuide({ dense }: { dense?: boolean }) {
  const items = [
    { primary: `Tamanho recomendado: ${BANNER_IDEAL.primarySize} (${BANNER_IDEAL.primaryRatio}).`, secondary: BANNER_IDEAL.safeArea },
    { primary: 'Mobile e recorte', secondary: BANNER_IDEAL.mobileNote },
    { primary: 'Peso e formato do ficheiro', secondary: BANNER_IDEAL.fileHint },
    { primary: 'Formatos aceites e limite de tamanho', secondary: BANNER_IDEAL.uploadPolicy },
    { primary: 'Texto e acessibilidade', secondary: BANNER_IDEAL.textHint },
    { primary: 'Contraste', secondary: BANNER_IDEAL.contrastHint },
  ];
  return (
    <List dense={dense} disablePadding sx={{ '& .MuiListItemIcon-root': { minWidth: 36 } }}>
      {items.map((item, i) => (
        <ListItem key={i} alignItems="flex-start" sx={{ py: dense ? 0.5 : 1, px: 0 }}>
          <ListItemIcon sx={{ mt: 0.25 }}>
            <InfoIcon color="info" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={item.primary} secondary={item.secondary} primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} secondaryTypographyProps={{ variant: 'body2', component: 'span' }} />
        </ListItem>
      ))}
    </List>
  );
}

const defaultForm: AppBannerPayload & { imageFile: File | null } = {
  title: '',
  imageUrl: '',
  link: '',
  order: 0,
  active: true,
  startDate: '',
  endDate: '',
  companyName: '',
  contact: '',
  imageFile: null,
};

const PLACEHOLDER_IMG = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect fill="%23e0e0e0" width="400" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="14"%3EEvento%3C/text%3E%3C/svg%3E';

function cloneHighlightList(list: EventResponse[]): EventResponse[] {
  return structuredClone(list) as EventResponse[];
}

function adminEventToResponse(ev: AdminEvent): EventResponse {
  return {
    _id: ev._id,
    title: ev.title,
    slug: ev.slug,
    description: ev.description ?? '',
    startDate: typeof ev.startDate === 'string' ? ev.startDate : String(ev.startDate),
    endDate: typeof ev.endDate === 'string' ? ev.endDate : String(ev.endDate),
    location: ev.location ?? '',
    address: ev.address ?? '',
    city: ev.city ?? '',
    state: ev.state ?? '',
    category: ev.category ?? '',
    capacity: ev.capacity ?? 0,
    isPaid: Boolean(ev.isPaid),
    status: (['draft', 'cancelled', 'completed'].includes(ev.status) ? ev.status : 'published') as EventResponse['status'],
    coverImage: ev.coverImage,
    galleryImages: ev.galleryImages,
    createdAt: ev.createdAt,
    updatedAt: ev.updatedAt,
    tickets: (ev.tickets ?? []).map((t) => ({
      _id: t._id,
      name: t.name,
      description: t.description,
      price: t.price,
      quantity: t.quantity,
      startSaleDate: t.startSaleDate,
      endSaleDate: t.endSaleDate,
      eventId: t.eventId,
    })),
    customFields: [],
  };
}

type CampaignAnalytics = {
  bannerId: string;
  title: string;
  companyName: string;
  totalImpressions: number;
  totalClicks: number;
  ctr: number;
  byDay: Array<{ date: string; impressions: number; clicks: number }>;
};

type AnalyticsData = {
  totalImpressions: number;
  totalClicks: number;
  ctr: number;
  byCampaign: CampaignAnalytics[];
  period: { startDate: string; days: number };
};

const AppParaApp: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [banners, setBanners] = useState<AppBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [highlightsLoading, setHighlightsLoading] = useState(true);
  const [highlights, setHighlights] = useState<EventResponse[]>([]);
  /** Última versão confirmada no servidor (para descartar rascunho local). */
  const [committedHighlights, setCommittedHighlights] = useState<EventResponse[]>([]);
  const [highlightPicker, setHighlightPicker] = useState<EventResponse | null>(null);
  const [publishedChoices, setPublishedChoices] = useState<EventResponse[]>([]);
  const [orderSaving, setOrderSaving] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsBannerId, setAnalyticsBannerId] = useState<string>('');
  const [analyticsDays, setAnalyticsDays] = useState<number>(30);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<AppBanner | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<AppBannerPayload & { imageFile: File | null }>(defaultForm);
  const [blobPreviewUrl, setBlobPreviewUrl] = useState<string | null>(null);
  const [imageFileError, setImageFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!form.imageFile) {
      setBlobPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(form.imageFile);
    setBlobPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.imageFile]);

  const loadBanners = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await appBannerService.list();
      setBanners(res.data ?? []);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Erro ao carregar banners');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHighlights = async () => {
    setHighlightsLoading(true);
    try {
      const [pub, orderRes, adminRes] = await Promise.all([
        eventService.getHighlightedEvents(),
        appBannerService.getHighlightedEventsOrder(),
        adminEventService.getAll({
          limit: 120,
          page: 1,
          sortBy: 'startDate',
          sortOrder: 'desc',
        }),
      ]);

      const publicEvents = (pub?.data ?? []) as EventResponse[];
      const orderIds = orderRes?.data?.eventIds ?? [];
      const rawAdmin = adminRes?.data?.events ?? [];
      const curatedAdmin = rawAdmin.filter((e) => e.status === 'published' || e.status === 'pre_published');
      const byId = new Map<string, EventResponse>();

      curatedAdmin.forEach((e) => {
        byId.set(e._id, adminEventToResponse(e));
      });
      publicEvents.forEach((e) => {
        if (!byId.has(e._id)) byId.set(e._id, e);
      });

      setPublishedChoices(curatedAdmin.map(adminEventToResponse));

      let merged: EventResponse[];
      if (orderIds.length > 0) {
        const ordered: EventResponse[] = [];
        const seen = new Set<string>();
        for (const id of orderIds) {
          const ev = byId.get(String(id));
          if (ev) {
            ordered.push(ev);
            seen.add(String(id));
          }
        }
        for (const e of publicEvents) {
          if (!seen.has(e._id)) {
            ordered.push(e);
            seen.add(e._id);
          }
        }
        merged = ordered;
      } else {
        merged = publicEvents;
      }

      setHighlights(merged);
      setHighlightPicker(null);
      setCommittedHighlights(cloneHighlightList(merged));
    } catch {
      setHighlights([]);
      setPublishedChoices([]);
      setCommittedHighlights([]);
    } finally {
      setHighlightsLoading(false);
    }
  };

  const highlightsDirty = useMemo(
    () =>
      highlights.length !== committedHighlights.length ||
      highlights.some((h, i) => h._id !== committedHighlights[i]?._id),
    [highlights, committedHighlights],
  );

  const saveHighlightsOrder = async () => {
    setOrderSaving(true);
    try {
      await appBannerService.updateHighlightedEventsOrder(highlights.map((e) => e._id));
      setCommittedHighlights(cloneHighlightList(highlights));
    } catch {
      loadHighlights();
    } finally {
      setOrderSaving(false);
    }
  };

  const discardHighlightsDraft = () => {
    setHighlights(cloneHighlightList(committedHighlights));
    setHighlightPicker(null);
  };

  useEffect(() => {
    loadBanners();
  }, []);

  useEffect(() => {
    loadHighlights();
  }, []);

  const handleHighlightDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const items = Array.from(highlights);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    setHighlights(items);
  };

  const handleAddHighlight = () => {
    if (!highlightPicker) return;
    if (highlights.some((h) => h._id === highlightPicker._id)) {
      setHighlightPicker(null);
      return;
    }
    setHighlights((prev) => [...prev, highlightPicker]);
    setHighlightPicker(null);
  };

  const handleRemoveHighlight = (id: string) => {
    setHighlights((prev) => prev.filter((h) => h._id !== id));
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await appBannerService.getAnalytics({
        bannerId: analyticsBannerId || undefined,
        days: analyticsDays,
      });
      setAnalytics(res.data ?? null);
    } catch {
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (tabIndex === 3) loadAnalytics();
  }, [tabIndex, analyticsBannerId, analyticsDays]);

  const openNew = () => {
    setImageFileError(null);
    setEditingBanner(null);
    setForm({
      ...defaultForm,
      order: banners.length,
      startDate: new Date().toISOString().slice(0, 10),
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    });
    setDialogOpen(true);
  };

  const openEdit = (banner: AppBanner) => {
    setImageFileError(null);
    setEditingBanner(banner);
    setForm({
      title: banner.title,
      imageUrl: banner.imageUrl,
      link: banner.link || '',
      order: banner.order,
      active: banner.active,
      startDate: banner.startDate ? banner.startDate.slice(0, 10) : '',
      endDate: banner.endDate ? banner.endDate.slice(0, 10) : '',
      companyName: banner.companyName || '',
      contact: banner.contact || '',
      imageFile: null,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBanner(null);
    setForm(defaultForm);
    setImageFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageFileError(null);
    const file = e.target.files?.[0];
    if (!file) {
      setForm((f) => ({ ...f, imageFile: null }));
      return;
    }
    const err = validateBannerUploadFile(file);
    if (err) {
      setImageFileError(err);
      setForm((f) => ({ ...f, imageFile: null }));
      e.target.value = '';
      return;
    }
    setForm((f) => ({ ...f, imageFile: file }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Preencha o título');
      return;
    }
    if (form.imageFile) {
      const uploadErr = validateBannerUploadFile(form.imageFile);
      if (uploadErr) {
        setImageFileError(uploadErr);
        return;
      }
    }
    if (!editingBanner && !form.imageFile && !form.imageUrl?.trim()) {
      setError('Envie a imagem do banner (upload)');
      return;
    }
    if (!form.startDate || !form.endDate) {
      setError('Preencha o período da campanha (data início e fim)');
      return;
    }
    if (!form.companyName.trim()) {
      setError('Preencha o nome da empresa');
      return;
    }
    if (!form.contact.trim()) {
      setError('Preencha o contato');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: AppBannerPayload = {
        title: form.title.trim(),
        imageUrl: form.imageUrl?.trim() || undefined,
        link: form.link?.trim() || undefined,
        order: form.order,
        active: form.active,
        startDate: form.startDate,
        endDate: form.endDate,
        companyName: form.companyName.trim(),
        contact: form.contact.trim(),
      };
      if (editingBanner) {
        await appBannerService.update(editingBanner._id, payload, form.imageFile);
      } else {
        await appBannerService.create(payload, form.imageFile);
      }
      handleCloseDialog();
      loadBanners();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Erro ao salvar campanha');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remover esta campanha de banner?')) return;
    setError(null);
    try {
      await appBannerService.remove(id);
      loadBanners();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Erro ao remover');
    }
  };

  const imagePreview = blobPreviewUrl || form.imageUrl || null;
  const showLargeFileHint = Boolean(form.imageFile && form.imageFile.size > BANNER_SOFT_SIZE_WARN_BYTES);

  const formatDate = (d: string) => (d ? new Date(d).toLocaleDateString('pt-BR') : '-');
  const formatEventDate = (start: string, end: string) => {
    const s = new Date(start).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    const e = new Date(end).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${s} – ${e}`;
  };
  const isEventActive = (endDate: string) => new Date(endDate) >= new Date();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AppsIcon />
            App para App
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Campanhas de banner e destaques de eventos exibidos no aplicativo
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Nova campanha (banner)
        </Button>
      </Box>

      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 3 }}>
        <Tab label="Campanhas de Banner" icon={<ImageIcon />} iconPosition="start" />
        <Tab label="Destaques dos Eventos" icon={<EventIcon />} iconPosition="start" />
        <Tab label="Tags dos Eventos" icon={<TrendingUpIcon />} iconPosition="start" />
        <Tab label="Resultados dos Banners" icon={<BarChartIcon />} iconPosition="start" />
      </Tabs>

      {tabIndex === 0 && (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ImageIcon />
          Campanhas de Banner do App
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cada item é uma campanha com período de exibição. O app exibe apenas campanhas ativas e dentro do período (GET /api/app/banners).
        </Typography>

        <Accordion variant="outlined" sx={{ mb: 2, bgcolor: 'grey.50' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
              <InfoIcon color="info" />
              Guia: imagem ideal para o app (desktop e mobile)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              O app usa a mesma imagem em várias larguras de ecrã. Estas recomendações reduzem cortes estranhos, texto ilegível e carregamento lento.
            </Typography>
            <BannerIdealGuide dense />
          </AccordionDetails>
        </Accordion>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : banners.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            Nenhuma campanha cadastrada. Clique em &quot;Nova campanha (banner)&quot; para adicionar.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {banners.map((banner) => (
              <Grid item xs={12} sm={6} md={4} key={banner._id}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={banner.imageUrl}
                    alt={banner.title}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="140"%3E%3Crect fill="%23ddd" width="200" height="140"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EImagem não disponível%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {banner.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {banner.companyName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Período: {formatDate(banner.startDate)} – {formatDate(banner.endDate)} • Ordem: {banner.order} {!banner.active && '• Inativo'}
                    </Typography>
                    {banner.link && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        Link: {banner.link}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <IconButton size="small" color="primary" onClick={() => openEdit(banner)} aria-label="Editar">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(banner._id)} aria-label="Excluir">
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
      )}

      {tabIndex === 2 && <EventTagsAdminPanel />}

      {tabIndex === 3 && (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BarChartIcon />
          Resultados dos Banners
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Visualizações e cliques são contabilizados automaticamente quando o app envia esses dados.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Campanha</InputLabel>
            <Select value={analyticsBannerId} onChange={(e) => setAnalyticsBannerId(e.target.value)} label="Campanha">
              <MenuItem value="">Todas as campanhas</MenuItem>
              {banners.map((b) => (
                <MenuItem key={b._id} value={b._id}>{b.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Período</InputLabel>
            <Select value={analyticsDays} onChange={(e) => setAnalyticsDays(Number(e.target.value))} label="Período">
              <MenuItem value={7}>Últimos 7 dias</MenuItem>
              <MenuItem value={14}>Últimos 14 dias</MenuItem>
              <MenuItem value={30}>Últimos 30 dias</MenuItem>
              <MenuItem value={90}>Últimos 90 dias</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={loadAnalytics} disabled={analyticsLoading}>
            {analyticsLoading ? <CircularProgress size={24} /> : 'Atualizar'}
          </Button>
        </Box>

        {analyticsLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : analytics ? (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ bgcolor: 'primary.50', borderColor: 'primary.200' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <VisibilityIcon color="primary" />
                      <Typography variant="overline" color="text.secondary">Visualizações</Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="bold">{analytics.totalImpressions.toLocaleString('pt-BR')}</Typography>
                    <Typography variant="caption" color="text.secondary">vezes exibido</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ bgcolor: 'success.50', borderColor: 'success.200' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <MouseIcon color="success" />
                      <Typography variant="overline" color="text.secondary">Cliques</Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="bold">{analytics.totalClicks.toLocaleString('pt-BR')}</Typography>
                    <Typography variant="caption" color="text.secondary">total de cliques</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ bgcolor: 'info.50', borderColor: 'info.200' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUpIcon color="info" />
                      <Typography variant="overline" color="text.secondary">CTR</Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="bold">{analytics.ctr.toFixed(2)}%</Typography>
                    <Typography variant="caption" color="text.secondary">taxa de cliques</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 2 }}>Por campanha</Typography>
            {analytics.byCampaign.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3 }}>
                Nenhuma campanha no período selecionado.
              </Typography>
            ) : (
              analytics.byCampaign.map((campaign) => (
                <Accordion key={campaign.bannerId} variant="outlined" sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%', pr: 2 }}>
                      <Typography fontWeight="bold">{campaign.title}</Typography>
                      {campaign.companyName && (
                        <Typography variant="body2" color="text.secondary">— {campaign.companyName}</Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                        <Chip size="small" label={`${campaign.totalImpressions} visualizações`} />
                        <Chip size="small" color="success" label={`${campaign.totalClicks} cliques`} />
                        <Chip size="small" color="info" label={`CTR ${campaign.ctr.toFixed(2)}%`} />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {campaign.byDay.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Nenhum dado por dia neste período.</Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Data</strong></TableCell>
                              <TableCell align="right"><strong>Visualizações</strong></TableCell>
                              <TableCell align="right"><strong>Cliques</strong></TableCell>
                              <TableCell align="right"><strong>CTR</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[...campaign.byDay].reverse().map((row) => {
                              const ctr = row.impressions > 0 ? ((row.clicks / row.impressions) * 100).toFixed(2) : '0.00';
                              return (
                                <TableRow key={row.date}>
                                  <TableCell>{new Date(row.date + 'T12:00:00').toLocaleDateString('pt-BR')}</TableCell>
                                  <TableCell align="right">{row.impressions.toLocaleString('pt-BR')}</TableCell>
                                  <TableCell align="right">{row.clicks.toLocaleString('pt-BR')}</TableCell>
                                  <TableCell align="right">{ctr}%</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </>
        ) : (
          <Typography color="text.secondary">Carregue os dados para visualizar os resultados.</Typography>
        )}
      </Paper>
      )}

      {tabIndex === 1 && (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon />
          Destaques dos Eventos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Inclua eventos <strong>publicados</strong> ou <strong>pré-publicados</strong> abaixo para montar a lista. Os que estão em vigência também entram automaticamente na API pública.
          Arraste os cards para reordenar. <strong>Só passa a valer no app e no site depois de «Salvar alterações»</strong> (visitantes só veem eventos <strong>publicados</strong>).
        </Typography>

        {!highlightsLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: highlights.length > 0 ? 3 : 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <Autocomplete<EventResponse, false, false, false>
                sx={{ minWidth: 280, flex: '1 1 280px' }}
                options={publishedChoices.filter((c) => !highlights.some((h) => h._id === c._id))}
                getOptionLabel={(o) => `${o.title} · ${new Date(o.startDate).toLocaleDateString('pt-BR')}`}
                value={highlightPicker}
                onChange={(_e, v) => setHighlightPicker(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Incluir evento nos destaques" placeholder="Buscar pelo título" />
                )}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                disabled={orderSaving || publishedChoices.length === 0}
              />
              <Button
                variant="outlined"
                disabled={!highlightPicker || orderSaving}
                onClick={handleAddHighlight}
                sx={{ alignSelf: 'flex-start', mt: { xs: 0, sm: 0.5 } }}
              >
                Adicionar à lista
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
              {highlightsDirty && (
                <Chip size="small" color="warning" label="Alterações não salvas" sx={{ mr: 'auto' }} />
              )}
              <Button variant="outlined" color="inherit" disabled={!highlightsDirty || orderSaving} onClick={discardHighlightsDraft}>
                Descartar alterações
              </Button>
              <Button variant="contained" disabled={!highlightsDirty || orderSaving} onClick={saveHighlightsOrder}>
                {orderSaving ? 'Salvando…' : 'Salvar alterações'}
              </Button>
            </Box>
          </Box>
        )}

        {highlightsLoading ? (
          <Grid container spacing={2}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card variant="outlined">
                  <Skeleton variant="rectangular" height={140} />
                  <CardContent>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="50%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : highlights.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
            <EventIcon sx={{ fontSize: 56, color: 'grey.400', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Lista vazia — use o campo acima para incluir eventos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Se houver eventos publicados em vigência, eles também entram na resposta pública; aqui você define a ordem e pode fixar quais aparecem primeiro.
            </Typography>
          </Box>
        ) : (
          <DragDropContext onDragEnd={handleHighlightDragEnd}>
            <Droppable droppableId="highlights">
              {(provided) => (
                <Grid
                  container
                  spacing={2}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{ minHeight: 200 }}
                >
                  {highlights.map((ev, index) => (
                    <Draggable key={ev._id} draggableId={ev._id} index={index}>
                      {(provided, snapshot) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{ display: 'flex' }}
                        >
                          <Card
                            variant="outlined"
                            sx={{
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'box-shadow 0.2s',
                              boxShadow: snapshot.isDragging ? 4 : 0,
                              opacity: snapshot.isDragging ? 0.9 : 1,
                              '&:hover': { boxShadow: 2 },
                            }}
                          >
                            <Box
                              {...provided.dragHandleProps}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: 1,
                                py: 0.5,
                                bgcolor: 'action.hover',
                                cursor: 'grab',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <DragIndicatorIcon fontSize="small" color="action" />
                              <Box sx={{ flex: 1 }} />
                              <IconButton
                                size="small"
                                aria-label="Remover dos destaques"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRemoveHighlight(ev._id);
                                }}
                                disabled={orderSaving}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Box sx={{ position: 'relative' }}>
                              <CardMedia
                                component="img"
                                height="140"
                                image={ev.coverImage || ev.galleryImages?.[0] || PLACEHOLDER_IMG}
                                alt={ev.title}
                                sx={{ objectFit: 'cover' }}
                                onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER_IMG; }}
                              />
                              <Chip
                                label={isEventActive(ev.endDate) ? 'Em vigência' : 'Finalizado'}
                                size="small"
                                color={isEventActive(ev.endDate) ? 'success' : 'default'}
                                sx={{ position: 'absolute', top: 8, right: 8 }}
                              />
                            </Box>
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {ev.title}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {formatEventDate(ev.startDate, ev.endDate)}
                                </Typography>
                              </Box>
                              {ev.location && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PlaceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    {ev.location}
                                  </Typography>
                                </Box>
                              )}
                              {ev.category && (
                                <Chip label={ev.category} size="small" sx={{ mt: 1 }} variant="outlined" />
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Grid>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Paper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBanner ? 'Editar campanha (banner)' : 'Nova campanha (banner)'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover', borderColor: 'info.light' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="info" fontSize="small" />
                Lembrete rápido — imagem ideal
              </Typography>
              <BannerIdealGuide dense />
            </Paper>

            <TextField
              label="Título da campanha"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              required
            />

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Imagem do banner
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                JPEG, PNG, WebP ou GIF — máximo {BANNER_UPLOAD_MAX_MB} MB. Sem conversão no servidor.
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept={BANNER_UPLOAD_ACCEPT}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth
                sx={{ mb: 1 }}
              >
                {form.imageFile ? form.imageFile.name : (editingBanner ? 'Trocar imagem' : 'Selecionar imagem')}
              </Button>
              {imageFileError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setImageFileError(null)}>
                  {imageFileError}
                </Alert>
              )}
              {showLargeFileHint && !imageFileError && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Este ficheiro tem mais de 1 MB; pode carregar mais devagar em redes móveis. Considere comprimir antes de enviar.
                </Alert>
              )}
              {editingBanner && !form.imageFile && (
                <TextField
                  label="Ou URL da imagem (se não trocar)"
                  value={form.imageUrl}
                  onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
                  fullWidth
                  size="small"
                  placeholder="https://..."
                />
              )}
              {imagePreview && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <CardMedia component="img" image={imagePreview} alt="Preview" sx={{ maxHeight: 120, objectFit: 'contain', borderRadius: 1 }} />
                </Box>
              )}
            </Box>

            <TextField
              label="Link (para onde o banner leva ao clicar)"
              value={form.link}
              onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
              fullWidth
              placeholder="https://..."
            />

            <TextField
              label="Nome da empresa"
              value={form.companyName}
              onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
              fullWidth
              required
            />

            <TextField
              label="Contato (e-mail ou telefone)"
              value={form.contact}
              onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
              fullWidth
              required
              placeholder="email@exemplo.com ou (00) 00000-0000"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Início da campanha"
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Fim da campanha"
                type="date"
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              label="Ordem de exibição"
              type="number"
              value={form.order}
              onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value, 10) || 0 }))}
              fullWidth
              inputProps={{ min: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
              }
              label="Campanha ativa (visível no app dentro do período)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : editingBanner ? 'Salvar' : 'Criar campanha'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AppParaApp;
